import { useMuiModalContext } from '@modules/common/components/Modal/ModalContext';
import ModalHeader from '@modules/common/components/Modal/ModalHeader';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { IntegrationNamePreview } from '@modules/integrations/components/IntegrationNamePreview';
import { trackIntegrationEvent } from '@modules/integrationsV2/utils';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  DialogActions,
  DialogContent,
  FormHelperText,
  TextField,
  Typography,
} from '@mui/material';
import {
  IntegrationChannelType,
  IntegrationDirection,
  IntegrationType,
} from 'generated/graphql';
import { IntegrationNameErrorMessages } from 'modules/ui/Settings/Integrations/utils/integrationErrorMessages';
import { ChangeEvent, useState } from 'react';
import ErrorCode from 'v2/core/errors/codes';
import { isErrorCode } from 'v2/core/errors/errors';
import useCreateIntegrationFormData from 'v2/core/services/integrations';
import { useGetUser } from 'v2/core/services/user';

interface FileUploadFormModalProps {
  integrationType: IntegrationType;
}

export const FileUploadFormModal = ({
  integrationType,
}: FileUploadFormModalProps) => {
  const {
    id: userID,
    tenant: { id: tenantID },
  } = useGetUser();
  const { closeModal } = useMuiModalContext();
  const { showErrorNotification } = useNotificationContext();

  const [integrationName, setIntegrationName] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { loading, createIntegration } = useCreateIntegrationFormData({
    tenantID,
    userID,
    state: IntegrationChannelType.FileUpload,
    integrationType,
    code: integrationName,
    direction: IntegrationDirection.Inbound,
    onError: (error: Error) => {
      if (isErrorCode(error, ErrorCode.DuplicateRecord)) {
        setErrorMessage(IntegrationNameErrorMessages.DUPLICATE_NAME);
        return;
      }
      showErrorNotification({
        contentComponent: `Something went wrong${
          error.message ? `: ${error.message}` : '!'
        }`,
        props: {},
      });
    },
    onCompleted: () => {
      closeModal();
      trackIntegrationEvent({
        eventName: 'Integration Added',
        integrationChannelType: IntegrationChannelType.FileUpload,
      });
    },
  });

  const handleSubmit = () => {
    createIntegration();
    trackIntegrationEvent({
      eventName: 'Integration Form Connect CTA Clicked',
      integrationChannelType: IntegrationChannelType.FileUpload,
    });
  };

  const onTextInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setIntegrationName(value);

    if (value.length > 100) {
      setErrorMessage(IntegrationNameErrorMessages.CHARACTER_LIMIT_EXCEEDED);
      return;
    }

    if (value.includes('-')) {
      setErrorMessage(IntegrationNameErrorMessages.DASH_UNSUPPORTED);
      return;
    }

    setErrorMessage(null);
  };

  const renderInputHelpText = () => {
    return errorMessage ? (
      <FormHelperText
        sx={{
          textAlign: 'left',
          color: 'error.light',
          marginTop: 1,
        }}
      >
        {errorMessage}
      </FormHelperText>
    ) : (
      <FormHelperText
        sx={{
          textAlign: 'right',
          marginTop: 1,
        }}
      >
        {integrationName.length} /100
      </FormHelperText>
    );
  };

  return (
    <>
      <ModalHeader title="File Upload" showCloseButton={true} />
      <DialogContent dividers>
        <Typography color="text.secondary" variant="body2">
          This will add a new file upload integration as a source. You can
          upload files to it after giving it a name.
        </Typography>
        <Box pt={2}>
          <TextField
            fullWidth
            error={Boolean(errorMessage)}
            label="File upload integration name"
            placeholder="Enter integration name"
            variant="outlined"
            value={integrationName}
            onChange={onTextInputChange}
            disabled={loading}
          />
          {renderInputHelpText()}
          <IntegrationNamePreview
            integrationName={integrationName}
            sourceName="File Upload"
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <LoadingButton
          fullWidth
          type="submit"
          size="large"
          variant="contained"
          loading={loading}
          disabled={!integrationName.length || Boolean(errorMessage)}
          onClick={handleSubmit}
        >
          Proceed
        </LoadingButton>
      </DialogActions>
    </>
  );
};
